import { html, render } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import LitElementLight from './light-element';
import BtuCalculatorService from '../../typescript/services/btu-calculator-service';
import { IBtuResult } from '../models/iBtuResult';
import Logger from '../../typescript/modules/logger';
import Helpers from '../../typescript/modules/helpers';

declare var Tooltip: any;
declare var popups: any;

@customElement('btu-calculator')
export class BTUCalculator extends LitElementLight {

	@state() private room: string = '';
	@state() private dimension: string = '';
	@state() private width: number | null = null;
	@state() private length: number | null = null;
	@state() private height: number | null = null;
	@state() private exposure: string = '';
	@state() private glazing: string = '';
	@state() private unit: 'm' | 'ft' = 'm';

	@state() private initialFacets: string = '';
	@state() private search: string = '';

	@state() private btuResult: {
		btuFrom: number;
		btuTo: number;
		wattFrom: number;
		wattTo: number;
	} | null = null;

	@state() private validationErrors: {
		room: boolean;
		dimensions: boolean;
		exposure: boolean;
		glazing: boolean;
	} = {
			room: false,
			dimensions: false,
			exposure: false,
			glazing: false
		};

	@state() private quickFilters = ['Type', 'Colour'];
	@state() private appliedFiltersToHide = ['Heat Output', 'Fuel Source'];
	@state() private showWattsResults: boolean = false;

	@property({ type: String })
	private resultsCategory: string;

	@property({ type: Boolean })
	private showZeroFilters: boolean;

	private roomOptions = this.initialiseRoomOptions();

	private initialiseRoomOptions() {
		const isToastySite = Helpers.activeSite() === "Toasty";
		const baseOptions = [
			{ key: 'bedroom', label: 'Bedroom', imageSrc: "/images/btu/btu-bedroom.jpg?v=21045", modifier: 3.8 },
			{ key: 'kitchen', label: 'Kitchen', imageSrc: "/images/btu/btu-kitchen.webp?v=21045", modifier: 3.8 },
			{ key: 'hallway', label: 'Hallway', imageSrc: "/images/btu/btu-hallway.webp?v=21045", modifier: 3.8 },
			{ key: 'bathroom', label: 'Bathroom', imageSrc: "/images/btu/btu-bathroom.webp?v=21045", modifier: 4.8 },
			{ key: 'living', label: 'Living room', imageSrc: "/images/btu/btu-living-room.webp?v=21045", modifier: 4.8 },
			{ key: 'dining', label: 'Dining room', imageSrc: "/images/btu/btu-dining-room.webp?v=21045", modifier: 4.8 },
			//{ key: 'conservatory', label: 'Conservatory', icon: this.diningRoomIcon, modifier: 6.2 }
		];

		if (isToastySite) {
			return baseOptions.map(option => {
				const toastyImages: { [key: string]: string } = {
					'bedroom': "/images/btu/btu-bedroom-toasty.webp?v=21045",
					'kitchen': "/images/btu/btu-kitchen-toasty.webp?v=21045",
					'hallway': "/images/btu/btu-hallway-toasty.webp?v=21045",
					'bathroom': "/images/btu/btu-bathroom-toasty.webp?v=21045",
					'living': "/images/btu/btu-living-room-toasty.webp?v=21045",
					'dining': "/images/btu/btu-dining-room-toasty.webp?v=21045",
				};
				return {
					...option,
					imageSrc: toastyImages[option.key] || option.imageSrc
				};
			});
		}

		return baseOptions;
	}

	private exposureOptions = [
		{ key: 'sheltered', label: 'Sheltered', desc: 'Internal room with no external walls', modifier: 0.85, imageSrc: "/images/btu/sheltered-room.png?v=21045" },
		{ key: 'average', label: 'Average', desc: '1 external wall', modifier: 1.0, imageSrc: "/images/btu/average-room.png?v=21045" },
		{ key: 'exposed', label: 'Exposed', desc: '2 external walls', modifier: 1.1, imageSrc: '/images/btu/exposed-room.png?v=21045' },
	];

	private glazingOptions = [
		{ key: 'single', label: 'Single glazing', modifier: 1, imageSrc: "/images/btu/single-glazing.png?v=21045" },
		{ key: 'double', label: 'Double glazing', modifier: 0.9, imageSrc: "/images/btu/double-glazing.png?v=21045" }
	];

	private btuCalculatorService = new BtuCalculatorService();
	private activeSite = Helpers.activeSite().toLowerCase();
	private TAP_BTU_FACET = 'HeatOutputBtu';
	private OTHERS_BTU_FACET = 'HeatOutput-BTU50';
	private TAP_BTU_WATTS_FACET = 'HeatOutputWATTS';
	private OTHERS_WATTS_FACET = 'HeatOutput-Watts';

	connectedCallback() {
		super.connectedCallback();
		setTimeout(() => {
			this.classList.add('has-loaded');
		}, 250);
	}

	private handleRoomSelect(key: string) {
		this.room = key;
		this.validationErrors.room = false;
	}

	private handleDimensionSelect(key: string) {
		this.dimension = key;
		const preset = this.getDimensionPresetsForRoom(this.room)[key];
		if (this.unit === 'm') {
			this.width = preset.width;
			this.length = preset.length;
			this.height = preset.height;
		} else {
			this.width = this.convertMetresToFeet(preset.width);
			this.length = this.convertMetresToFeet(preset.length);
			this.height = this.convertMetresToFeet(preset.height);
		}
		this.validationErrors.dimensions = false;
	}

	private convertMetresToFeet(valueInMetres: number): number {
		return Math.round(valueInMetres * 3.28084);
	}

	private handleInputChange(e: Event, field: 'width' | 'length' | 'height') {
		const value = parseFloat((e.target as HTMLInputElement).value);
		if (!isNaN(value)) {
			// Enforce maximum value of 9999 to make QA happy
			const clampedValue = Math.min(value, 9999);
			this[field] = clampedValue;

			if (clampedValue !== value) {
				(e.target as HTMLInputElement).value = clampedValue.toString();
			}

			// Clear dimensions validation error if all dimensions are now valid
			if (this.width && this.width > 0 && this.length && this.length > 0 && this.height && this.height > 0) {
				this.validationErrors.dimensions = false;
			}
		}
	}

	private handleExposureSelect(key: string) {
		this.exposure = key;
		this.validationErrors.exposure = false;
	}

	private handleGlazingSelect(key: string) {
		this.glazing = key;
		this.validationErrors.glazing = false;
	}

	private handleUnitToggle(unit: 'm' | 'ft') {
		if (this.unit === unit) return;
		if (unit === 'ft') {
			this.width = this.width != null ? +(this.width * 3.28084).toFixed(2) : null;
			this.length = this.length != null ? +(this.length * 3.28084).toFixed(2) : null;
			this.height = this.height != null ? +(this.height * 3.28084).toFixed(2) : null;
		} else {
			this.width = this.width != null ? +(this.width / 3.28084).toFixed(2) : null;
			this.length = this.length != null ? +(this.length / 3.28084).toFixed(2) : null;
			this.height = this.height != null ? +(this.height / 3.28084).toFixed(2) : null;
		}
		this.unit = unit;
	}

	private calculateBtuResult(): Promise<IBtuResult | null> {

		this.clearSearchParams();
		const hasValidationErrors = this.validateForm();

		if (hasValidationErrors) {
			this.scrollToFirstError();
			return null;
		}

		this.clearValidationErrors();

		const roomDetails = {
			length: this.length!,
			width: this.width!,
			height: this.height!,
			roomTypeModifier: this.getRoomTypeModifier(this.room),
			windowTypeModifier: this.getWindowTypeModifier(this.glazing),
			roomExposureModifier: this.getRoomExposureModifier(this.exposure),
		};

		const result = this.btuCalculatorService.calculate(roomDetails, this.unit === 'ft' ? 1 : 2);

		this.btuResult = result;

		setTimeout(() => {
			this.renderResultsToContainer();
			const resultsWrapper = document.querySelector('.c-btu-results');
			if (resultsWrapper) {
				resultsWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
			Tooltip.init(true);
		}, 200);

		try {
			this.saveResult(result);
		} catch (error) {
			Logger.trackException(error, 'BTU calculator save result');
		}
	}

	private renderResultsToContainer(): void {
		const container = document.querySelector('.c-btu-results__container') as HTMLElement;
		const resultsWrapper = document.querySelector('.c-btu-results');
		if (!container || !resultsWrapper) return;

		const resultsTemplate = this.renderResults();
		render(resultsTemplate, container);
		resultsWrapper.classList.remove('c-btu-results--hidden');
	}

	private validateForm(): boolean {
		let hasErrors = false;

		// Validate room selection
		if (!this.room) {
			this.validationErrors.room = true;
			hasErrors = true;
		}

		// Validate dimensions
		if (this.width == null || this.length == null || this.height == null ||
			this.width <= 0 || this.length <= 0 || this.height <= 0) {
			this.validationErrors.dimensions = true;
			hasErrors = true;
		}

		// Validate exposure
		if (!this.exposure) {
			this.validationErrors.exposure = true;
			hasErrors = true;
		}

		// Validate glazing
		if (!this.glazing) {
			this.validationErrors.glazing = true;
			hasErrors = true;
		}

		this.requestUpdate();
		return hasErrors;
	}

	private clearValidationErrors(): void {
		this.validationErrors = {
			room: false,
			dimensions: false,
			exposure: false,
			glazing: false
		};
		this.requestUpdate();
	}

	private scrollToFirstError(): void {
		setTimeout(() => {
			const firstErrorRow = this.querySelector('.c-btu__row--error');
			if (firstErrorRow) {
				firstErrorRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}, 200);
	}

	private saveResult(result: IBtuResult) {
		const resultString = JSON.stringify(result);
		localStorage.setItem('btuCalculatorResult', resultString);
	}

	private getRoomTypeModifier(roomKey: string): number {
		const room = this.roomOptions.find(opt => opt.key === roomKey);
		return room ? room.modifier : 1;
	}

	private getRoomExposureModifier(exposureKey: string): number {
		const exposure = this.exposureOptions.find(opt => opt.key === exposureKey);
		return exposure ? exposure.modifier : 1;
	}

	private getWindowTypeModifier(glazingKey: string): number {
		const glazing = this.glazingOptions.find(opt => opt.key === glazingKey);
		return glazing ? glazing.modifier : 1;
	}

	private resetCalculator() {
		popups.loader();
		window.scrollTo(0, 0);
		const url = new URL(window.location.href);
		url.search = '';
		window.location.href = url.toString();
	}

	private clearSearchParams() {
		const url = new URL(window.location.href);
		url.search = '';
		window.history.replaceState({}, document.title, url.toString());
	}

	private formatNumber(number: number): string {
		if (!number) {
			return '0';
		}
		return number.toLocaleString();
	}

	private getDimensionPresetsForRoom(roomKey: string) {
		const presetsByRoom = {
			bedroom: {
				small: { width: 3, length: 3, height: 2.2, imageSrc: `/images/btu/small-room-${this.activeSite}.png?v=21045` },
				medium: { width: 5, length: 4, height: 2.4, imageSrc: `/images/btu/medium-room-${this.activeSite}.png?v=21045` },
				large: { width: 6, length: 6, height: 2.7, imageSrc: `/images/btu/large-room-${this.activeSite}.png?v=21045` },
			},
			kitchen: {
				small: { width: 3, length: 2, height: 2.2, imageSrc: `/images/btu/small-room-${this.activeSite}.png?v=21045` },
				medium: { width: 5, length: 3, height: 2.4, imageSrc: `/images/btu/medium-room-${this.activeSite}.png?v=21045` },
				large: { width: 6, length: 6, height: 2.7, imageSrc: `/images/btu/large-room-${this.activeSite}.png?v=21045` },
			},
			hallway: {
				small: { width: 1.5, length: 3, height: 2.2, imageSrc: `/images/btu/small-room-${this.activeSite}.png?v=21045` },
				medium: { width: 2, length: 3, height: 2.4, imageSrc: `/images/btu/medium-room-${this.activeSite}.png?v=21045` },
				large: { width: 2, length: 4, height: 2.7, imageSrc: `/images/btu/large-room-${this.activeSite}.png?v=21045` },
			},
			bathroom: {
				small: { width: 2, length: 2, height: 2.2, imageSrc: `/images/btu/small-room-${this.activeSite}.png?v=21045` },
				medium: { width: 3, length: 2, height: 2.4, imageSrc: `/images/btu/medium-room-${this.activeSite}.png?v=21045` },
				large: { width: 3, length: 3, height: 2.7, imageSrc: `/images/btu/large-room-${this.activeSite}.png?v=21045` },
			},
			living: {
				small: { width: 3, length: 4, height: 2.2, imageSrc: `/images/btu/small-room-${this.activeSite}.png?v=21045` },
				medium: { width: 4, length: 5, height: 2.4, imageSrc: `/images/btu/medium-room-${this.activeSite}.png?v=21045` },
				large: { width: 5, length: 6, height: 2.7, imageSrc: `/images/btu/large-room-${this.activeSite}.png?v=21045` },
			},
			dining: {
				small: { width: 3, length: 3, height: 2.2, imageSrc: `/images/btu/small-room-${this.activeSite}.png?v=21045` },
				medium: { width: 4, length: 4, height: 2.4, imageSrc: `/images/btu/medium-room-${this.activeSite}.png?v=21045` },
				large: { width: 5, length: 6, height: 2.7, imageSrc: `/images/btu/large-room-${this.activeSite}.png?v=21045` },
			},
			default: {
				small: { width: 3, length: 3, height: 2.2, imageSrc: `/images/btu/small-room-${this.activeSite}.png?v=21045` },
				medium: { width: 4, length: 5, height: 2.4, imageSrc: `/images/btu/medium-room-${this.activeSite}.png?v=21045` },
				large: { width: 6, length: 7, height: 2.7, imageSrc: `/images/btu/large-room-${this.activeSite}.png?v=21045` },
			}
		};
		return presetsByRoom[roomKey] || presetsByRoom.default;
	}

	private getBtuFacetName(): string {
		if (this.showWattsResults) {
			return this.activeSite === 'tapwarehouse' ? this.TAP_BTU_WATTS_FACET : this.OTHERS_WATTS_FACET;
		} else {
			return this.activeSite === 'tapwarehouse' ? this.TAP_BTU_FACET : this.OTHERS_BTU_FACET;
		}
	}

	private getInitialFacets(): string {
		const params = new URLSearchParams();
		params.set('showParents', 'false');

		if (!this.btuResult) {
			return params.toString();
		}

		const minValue = this.calculatorMinValue(this.showWattsResults ? this.btuResult?.wattFrom || 0 : this.btuResult?.btuFrom || 0);
		const maxValue = this.calculatorMaxValue(this.showWattsResults ? this.btuResult.wattTo : this.btuResult.btuTo);

		const facetName = this.getBtuFacetName();
		params.set(`min-${facetName}`, minValue.toString());
		params.set(`max-${facetName}`, maxValue.toString());

		if (this.showWattsResults) {
			while (params.has('FuelSource')) {
				params.delete('FuelSource');
			}
			params.append('FuelSource', 'Electric');
			params.append('FuelSource', 'Dual Fuel');
		} else {
			while (params.has('FuelSource')) {
				params.delete('FuelSource');
			}
		}

		return params.toString();
	}

	private calculatorMinValue(value: number): number {
		return Math.ceil(value);
	}

	private calculatorMaxValue(value: number): number {
		return Math.ceil(value * 1.25);
	}

	private getInitialResultsCategory(): string {
		return this.resultsCategory;
	}

	private onElectricToggleChange = (e: Event) => {
		const isChecked = (e.target as HTMLInputElement).checked;
		if (!isChecked) {
			// remove any existing FuelSource facets from the URL
			const url = new URL(window.location.href);
			const params = url.searchParams;
			if (params.has('FuelSource')) {
				while (params.has('FuelSource')) {
					params.delete('FuelSource');
				}
				window.history.replaceState({}, document.title, url.toString());
			}
		}
		this.showWattsResults = isChecked;
		this.renderResultsToContainer();
	}

	private renderResults() {
		return html`
			<div class="c-btu__results" role="status">
				<h2 class="c-btu__results-title">Your BTU calculation</h2>
				<span class="c-btu__results-item">
					<span class="c-btu__results-item-prefix">BTU <span class="c-btu__results-item-prefix-small">∆t 50ºC</span>
						<a href="#tooltip-BTU-result" class="js-tooltip-trigger js-tooltip-trigger--alt" data-js-tooltip-trigger><tooltip-trigger-icon></tooltip-trigger-icon><span class="u-visuallyhidden">More information</span></a>
					</span> <strong class="js-btu-from" data-test="btu-calculator-btu50-from">${this.formatNumber(this.btuResult ? this.btuResult.btuFrom : 0)}</strong> &ndash; <strong class="js-btu-to" data-test="btu-calculator-btu50-to">${this.formatNumber(this.btuResult ? this.btuResult.btuTo : 0)}</strong></span>
				<span class="c-btu__results-item">
					<span class="c-btu__results-item-prefix">Watts <span class="c-btu__results-item-prefix-small">∆t 50ºC</span>
					<a href="#tooltip-watts-result" class="js-tooltip-trigger js-tooltip-trigger--alt" data-js-tooltip-trigger><tooltip-trigger-icon></tooltip-trigger-icon><span class="u-visuallyhidden">More information</span></a>
				</span> <strong class="js-btu-watts-from" data-test="btu-calculator-watts-from">${this.formatNumber(this.btuResult ? this.btuResult.wattFrom : 0)}</strong> &ndash; <strong class="js-btu-watts-to" data-test="btu-calculator-watts-to">${this.formatNumber(this.btuResult ? this.btuResult.wattTo : 0)}</strong></span>
				<button type="button" class="c-button c-button--medium c-button--secondary" data-test="btu-calculator-reset" @click=${() => this.resetCalculator()}>Reset calculator</button>
			</div>

			<div class="js-tooltip" id="tooltip-BTU-result" data-js-tooltip data-test="tooltip-overlay">
				<div class="js-tooltip__inner" data-js-tooltip-inner>
					<div class="cms">
						<p><strong>BTU</strong></p>
						<p>BTU (British Thermal Units) is a measure of the heat output of radiators. ∆t 50ºC is the standard output of boilers in the UK & Europe, so this figure is an indication of the heat output (BTU) your radiator will give you at this standard temperature.</p>
					</div>
				</div>
			</div>

			<div class="js-tooltip" id="tooltip-watts-result" data-js-tooltip data-test="tooltip-overlay">
				<div class="js-tooltip__inner" data-js-tooltip-inner>
					<div class="cms">
						<p><strong>Watts</strong></p>
						<p>Watts is a measure of electrical power. It indicates the rate at which energy is used or produced. In the context of heating, it can be used to describe the power output of a radiator or heating system.</p>
					</div>
				</div>
			</div>

			<div class="c-btu__results-products js-filter-scroll-after-change">
				<quick-filters .filtersToShow="${this.quickFilters}" .appliedFiltersToHide="${this.appliedFiltersToHide}" showClearAll>
				<div slot="filters-end" class="c-quick-filters__item">
					<label class="c-toggle" for="shop-electric">
						<span class="c-toggle__label">
							Shop Electric
						</span>
						<input type="checkbox" name="shop-electric" id="shop-electric" class="c-toggle__input" data-test="btu-electric-toggle" @change=${this.onElectricToggleChange} >
						<span class="c-toggle__slider"></span>
					</label>
				</div>
				</quick-filters>
				<range-block search="${this.search || this.getInitialResultsCategory()}" initialFacets="${this.initialFacets || this.getInitialFacets()}" ?showZeroFilters=${this.showZeroFilters}></range-block>
			</div>
		`;
	}

	render() {
		const dimensionPresets = this.getDimensionPresetsForRoom(this.room);
		return html`
			<div class="c-cms">
				<h2 class="c-btu__title">Enter your room details</h2>
			</div>
			<div class="c-btu">
				<div class="c-btu__row ${this.validationErrors.room ? 'c-btu__row--error' : ''}">
					<div class="c-btu__row-question">
						<h2 class="c-btu__question-title">1) Which room would you like to heat?</h2>
						<p class="c-btu__explanation">Choose the room type to help us estimate your heating needs.</p>
					</div>
					<div class="c-btu__row-body">
						<div class="c-btu__option-button-group c-btu__option-button-group--room">
							${this.roomOptions.map(opt => html`
								<button type="button" data-test="btu-calculator-room-${opt.key}" class="c-btu__option-button ${this.room === opt.key ? 'c-btu__option-button--selected' : ''}" @click=${() => this.handleRoomSelect(opt.key)}>
									${opt.imageSrc ? html`<img src="${opt.imageSrc}" alt="" class="c-btu__option-button-icon">` : ''}
									<span>${opt.label}</span>
								</button>
							`)}
						</div>
						${this.validationErrors.room ? html`
							<p data-test="btu-calculator-error-room" class="c-btu__validation-error c-form__error">Please select a room type</p>
						` : ''}
					</div>
				</div>

				<div class="c-btu__row ${this.validationErrors.dimensions ? 'c-btu__row--error' : ''}">

					<div class="c-btu__row-question">
						<h2 class="c-btu__question-title">2) What are the dimensions of the room?</h2>
						<p class="c-btu__explanation">Choose a estimated room size or enter your own dimensions for a more accurate calculation.</p>
					</div>

					<div class="c-btu__row-body">
						<div style="display: flex; align-items: center; gap: 1rem;" class="u-mb20">
							<span><strong>Units:</strong></span>
							<button type="button" data-test="btu-calculator-unit-metres" class="c-btu__option-button c-btu__option-button--small ${this.unit === 'm' ? 'c-btu__option-button--selected' : ''}" @click=${() => this.handleUnitToggle('m')}>Metres</button>
							<button type="button" data-test="btu-calculator-unit-feet" class="c-btu__option-button c-btu__option-button--small ${this.unit === 'ft' ? 'c-btu__option-button--selected' : ''}" @click=${() => this.handleUnitToggle('ft')}>Feet</button>
						</div>
						<div class="c-btu__option-button-group c-btu__option-button-group--dimension u-mb20">
							${Object.entries(dimensionPresets as Record<string, { width: number; length: number; height: number; imageSrc: string }>).map(([key, preset]) => {
								let width = preset.width;
								let length = preset.length;
								let height = preset.height;
								let unitLabel = 'm';
								if (this.unit === 'ft') {
									width = this.convertMetresToFeet(width);
									length = this.convertMetresToFeet(length);
									height = this.convertMetresToFeet(height);
									unitLabel = 'ft';
								}
								return html`
									<button type="button" data-test="btu-calculator-size-${key}" class="c-btu__option-button ${this.dimension === key ? 'c-btu__option-button--selected' : ''}" @click=${() => this.handleDimensionSelect(key)}>
										<img alt="" class="c-btu__option-button-icon" src="${preset.imageSrc}" />
										${key.charAt(0).toUpperCase() + key.slice(1)}<br>
										<span class="c-btu__option-button-description">${width}${unitLabel} x ${length}${unitLabel} x ${height}${unitLabel}</span>
									</button>
								`;
							})}
						</div>
						<div class="c-btu__inputs">
							<div class="c-btu__inputs-group">
								<label for="roomWidth" class="c-form__label">Room width (${this.unit === 'm' ? 'metres' : 'feet'})</label>
								<input class="c-form__input c-btu__input" data-test="btu-calculator-dimensions-width" name="roomWidth" id="roomWidth" type="number" min="1" step="0.1" max="9999" .value=${this.width == null ? '' : String(this.width)} @input=${(e: Event) => this.handleInputChange(e, 'width')} placeholder="Your room's width">
							</div>
							<div class="c-btu__inputs-group">
								<label for="roomLength" class="c-form__label">Room length (${this.unit === 'm' ? 'metres' : 'feet'})</label>
								<input class="c-form__input c-btu__input" data-test="btu-calculator-dimensions-length" name="roomLength" id="roomLength" type="number" min="1" step="0.1" max="9999" .value=${this.length == null ? '' : String(this.length)} @input=${(e: Event) => this.handleInputChange(e, 'length')} placeholder="Your room's length">
							</div>
							<div class="c-btu__inputs-group">
								<label for="roomHeight" class="c-form__label">Room height (${this.unit === 'm' ? 'metres' : 'feet'})</label>
								<input class="c-form__input c-btu__input" data-test="btu-calculator-dimensions-height" name="roomHeight" id="roomHeight" type="number" min="1" step="0.1" max="9999" .value=${this.height == null ? '' : String(this.height)} @input=${(e: Event) => this.handleInputChange(e, 'height')} placeholder="Your room's height">
							</div>
						</div>
						${this.validationErrors.dimensions ? html`
							<p data-test="btu-calculator-error-dimensions" class="c-btu__validation-error c-form__error">Please enter dimensions for width, length, and height</p>
						` : ''}
					</div>

				</div>

				<div class="c-btu__row ${this.validationErrors.exposure ? 'c-btu__row--error' : ''}">
					<div class="c-btu__row-question">
						<h2 class="c-btu__question-title">3) How exposed is the room?</h2>
						<p class="c-btu__explanation">Rooms with more external walls or less shelter need more heat.</p>
					</div>
					<div class="c-btu__row-body">
						<div class="c-btu__option-button-group c-btu__option-button-group--exposure">
							${this.exposureOptions.map(opt => html`
								<button type="button" data-test="btu-calculator-exposure-${opt.key}" class="c-btu__option-button ${this.exposure === opt.key ? 'c-btu__option-button--selected' : ''}" @click=${() => this.handleExposureSelect(opt.key)}>
									<img alt="" class="c-btu__option-button-icon" src="${opt.imageSrc}" />
									${opt.label}
									<span class="c-btu__option-button-description">${opt.desc}</span>
								</button>
							`)}
						</div>
						${this.validationErrors.exposure ? html`
							<p data-test="btu-calculator-error-exposure" class="c-btu__validation-error c-form__error">Please select how exposed your room is</p>
						` : ''}
					</div>
				</div>

				<div class="c-btu__row ${this.validationErrors.glazing ? 'c-btu__row--error' : ''}">
					<div class="c-btu__row-question">
						<h2 class="c-btu__question-title">4) What window type do you have?</h2>
						<p class="c-btu__explanation">Double glazing keeps more heat in than single glazing.</p>
					</div>
					<div class="c-btu__row-body">
						<div class="c-btu__option-button-group c-btu__option-button-group--glazing">
							${this.glazingOptions.map(opt => html`
								<button type="button" data-test="btu-calculator-glazing-${opt.key}" class="c-btu__option-button ${this.glazing === opt.key ? 'c-btu__option-button--selected' : ''}" @click=${() => this.handleGlazingSelect(opt.key)}>
									<img alt="" class="c-btu__option-button-icon" src="${opt.imageSrc}" />
									${opt.label}
								</button>
							`)}
						</div>
						${this.validationErrors.glazing ? html`
							<p data-test="btu-calculator-error-glazing" class="c-btu__validation-error c-form__error">Please select your window type</p>
						` : ''}
					</div>
				</div>
			</div>

			<div class="c-btu__actions">
				<button type="button" data-test="btu-calculator-submit" class="c-button c-button--medium" @click=${this.calculateBtuResult}>Calculate results</button>
			</div>
	`;
	}
}

export default BTUCalculator;
